/**
 * planningDefaults.js v1.0.0 - 2026-10-09
 *
 * Documented defaults for planner inputs that can be missing from a saved scenario.
 * CALC-010: these used to be `value || <literal>` fallbacks scattered through the calculation code,
 * with different literals for the same input (operation duration was 7 in some modules, 30 in others,
 * 10 in Tab 2). Each default now lives here once, and every use is reported to the "assumptions used"
 * ledger (calculationEngine/assumptionLedger.js), which Tab 5 shows to the planner.
 *
 * APP_RULES Rule 6.1: an optional input with a documented default is acceptable; a REQUIRED input must
 * throw. Only inputs with a sensible default belong here.
 *
 * Fields:
 *   value       the assumed value
 *   unit        display unit ('' when unitless)
 *   label       short English description (translated in locales: tab5.assumptions.items.<id>)
 *   set_in      tab numbers where the planner sets it (display only); [] = not settable in the UI yet
 *   source      where the default comes from; 'carried over from code literal' = source still to be confirmed
 *
 * Version history:
 *   v1.0.0 (2026-10-09): initial release (CALC-010)
 */

export const PLANNING_DEFAULTS = {
  duration_days: {
    value: 10, unit: 'days', label: 'Operation duration', set_in: [2],
    source: 'Tab 2 pre-selected duration (Tab2_Operations.jsx). Other modules used 7 or 30.'
  },
  evacuation_paradigm: {
    value: 'CONTESTED', unit: '', label: 'Evacuation paradigm', set_in: [2],
    source: 'Most common Ukraine 2022-2024 condition (evacuationParadigms.js CONTESTED.ukraine_data).'
  },
  terrain: {
    value: 'MIXED', unit: '', label: 'Terrain', set_in: [2],
    source: 'Tab 2 pre-selected terrain (operationsConfig DEFAULT_ENVIRONMENT).'
  },
  acclimatization_complete: {
    value: true, unit: '', label: 'Troops acclimatized to the heat', set_in: [2],
    source: 'Tab 2 pre-selected (the force is assumed acclimatized); only matters in extreme heat (heatInjuryModule.js, docs/research/RESEARCH_WEATHER_INJURIES.md).'
  },
  cold_protection: {
    value: 'STANDARD', unit: '', label: 'Cold-weather protection of the troops', set_in: [2],
    source: 'Educated guess: modern issue clothing and boots, factor 0.25 of the WWII winter 1944-45 cold injury ratio (coldInjuryModule.js, docs/research/RESEARCH_WEATHER_INJURIES.md).'
  },
  weather: {
    value: 'CLEAR', unit: '', label: 'Weather', set_in: [2],
    source: 'Tab 2 pre-selected weather (operationsConfig DEFAULT_ENVIRONMENT).'
  },
  brigade_strength: {
    value: 4500, unit: 'personnel', label: 'Brigade strength', set_in: [1],
    source: 'Carried over from code literal (NATO brigade planning figure); confirm.'
  },
  force_total_strength: {
    value: 5000, unit: 'personnel', label: 'Total force strength', set_in: [1],
    source: 'Carried over from code literal; confirm.'
  },
  role2_distance_km: {
    value: 25, unit: 'km', label: 'Role 2B distance from the FEBA', set_in: [3],
    source: 'AJMedP-1 planning norm (medevacModule note: R2B default 25 km).'
  },
  role1_to_role2b_km: {
    value: 15, unit: 'km', label: 'Role 1 to Role 2B evacuation distance', set_in: [3],
    source: 'Carried over from code literal ("doctrinal default km"); confirm.'
  },
  brigade_position_feba_km: {
    value: 20, unit: 'km', label: 'Brigade position behind the FEBA', set_in: [1, 2],
    source: 'Carried over from code literal; the hierarchy extractor used 15 - reconcile.'
  },
  brigade_position_feba_km_hierarchy: {
    value: 15, unit: 'km', label: 'Brigade position behind the FEBA (facility hierarchy)', set_in: [1, 2],
    source: 'Carried over from code literal; the casualty calculator used 20 - reconcile.'
  },
  surgical_capacity_per_day: {
    value: 50, unit: 'patients/day', label: 'Surgical capacity', set_in: [3],
    source: 'Carried over from code literal; confirm.'
  },
  surgical_teams: {
    value: 2, unit: 'teams', label: 'Surgical teams', set_in: [3],
    source: 'Carried over from code literal; confirm.'
  },
  blood_stock_prbc: {
    value: 100, unit: 'units', label: 'Initial PRBC stock', set_in: [3, 4],
    source: 'Carried over from code literal; confirm.'
  },
  beds_per_personnel: {
    value: 0.03, unit: 'fraction', label: 'Medical beds per personnel', set_in: [3],
    source: 'Carried over from code literal (3%); confirm.'
  },
  medical_days_of_supply: {
    value: 7, unit: 'days', label: 'Initial medical days of supply', set_in: [4],
    source: 'Carried over from code literal; confirm.'
  },
  blood_days_of_supply: {
    value: 5, unit: 'days', label: 'Blood days of supply (MASCAL status)', set_in: [3, 4],
    source: 'Carried over from code literal; confirm.'
  },
  ambient_temperature_c: {
    value: 15, unit: 'C', label: 'Ambient temperature', set_in: [],
    source: 'Carried over from code literal; confirm.'
  },
  wbb_activation_threshold_percent: {
    value: 30, unit: '% of stock', label: 'Walking blood bank activation threshold', set_in: [4],
    source: 'Carried over from code literal; confirm.'
  },
  cross_division_overflow_threshold_percent: {
    value: 80, unit: '% occupancy', label: 'Cross-division routine overflow threshold', set_in: [2],
    source: 'Carried over from code literal; confirm.'
  },
  cross_division_max_capacity_percent: {
    value: 30, unit: '% of capacity', label: 'Cross-division maximum capacity share', set_in: [2],
    source: 'Carried over from code literal; confirm.'
  },
  thp_policy_days: {
    value: 7, unit: 'days', label: 'Theatre patient return policy (days)', set_in: [3],
    source: 'Carried over from code literal; confirm.'
  },
  psychiatric_support_level: {
    value: 'STANDARD', unit: '', label: 'Psychiatric support level', set_in: [3],
    source: 'Reference level of the sourced rates (US brigade behavioural health team, Belmont 2010). Choose the level in Tab 3.'
  },
  engagement_level: {
    value: 'FULL', unit: '', label: 'Engagement level', set_in: [2],
    source: 'Tab 2 default (full engagement, 100% of calculated casualties). Choose the level in Tab 2.'
  },
  resistance_level: {
    value: 'MODERATE', unit: '', label: 'Enemy resistance of a phase', set_in: [2],
    source: 'Moderate resistance (enemy combat power index 1.0), chosen by Dr. Tatar 2026-10-13. Set the resistance per phase in Tab 2.'
  },
  operation_type: {
    value: 'GROUND_COMBAT', unit: '', label: 'Operation type', set_in: [2],
    source: 'Tab 2 default (general ground combat, multiplier 1.0). Choose the operation type in Tab 2.'
  },
  rear_area_security_modifier: {
    value: 1.0, unit: '', label: 'Rear-area security modifier (national standard not known)', set_in: [1],
    source: 'NATO baseline (1.00) when the force carries no national standard. Choose the national standard in Tab 1.'
  },
  evac_route_surface: {
    value: 'PAVED', unit: '', label: 'Surface of the Role 1 to Role 2B route', set_in: [3],
    source: 'Tab 3 section B.3 default route (MSR Alpha, paved). Set the surface per route in Tab 3.'
  },
  evac_capacity_from_force: {
    value: 'from force', unit: '', label: 'Evacuation capacity (no fleet configured in Tab 3)', set_in: [3],
    source: 'Used only when Tab 3 has no ambulance or helicopter fleet: the capacity the MEDEVAC analysis derives from the force. Configure the fleet in Tab 3.'
  },
  hazard_antibiotics_in_stock: {
    value: 'in stock', unit: '', label: 'Antibiotics availability (wound hazard model)', set_in: [4],
    source: 'Used only when Tab 4 has no antibiotics stock figure: care is assumed in place. Enter the stock in Tab 4.'
  },
  hazard_dressings_in_stock: {
    value: 'in stock', unit: '', label: 'Dressings availability (wound hazard model)', set_in: [4],
    source: 'Used only when Tab 4 has no dressings stock figure: care is assumed in place. Enter the stock in Tab 4.'
  }
};
